import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  format,
  isSameMonth,
  isToday,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import api from '@/lib/api';
import { getUserColor } from '@/utils/calendarHelpers';
import { MiniCalendar } from './MiniCalendar';
import type { DayData } from '@/components/calendar/types';

export function PlannedVsActualView() {
  const [currentDate, setCurrentDate] = useState(() => startOfMonth(new Date()));

  const monthName = format(currentDate, 'MMMM yyyy');

  // Visible date range (including padding for calendar grid)
  const visibleDateRange = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return {
      startDate: format(calendarStart, 'yyyy-MM-dd'),
      endDate: format(calendarEnd, 'yyyy-MM-dd'),
    };
  }, [currentDate]);

  // Fetch live availability windows
  const { data: availabilityData = [] } = useQuery({
    queryKey: ['availability', visibleDateRange.startDate, visibleDateRange.endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: visibleDateRange.startDate,
        end_date: visibleDateRange.endDate,
      });
      const res = await api.get(`/availability/windows?${params}`);
      const flattened: any[] = [];
      (res.data.availability || []).forEach((day: any) => {
        (day.windows || []).forEach((w: any) => {
          flattened.push({
            date: day.date,
            start_time: w.start,
            end_time: w.end,
            auto_assign_user_id: w.auto_assign_user_id || null,
            auto_assign_user: w.auto_assign_user || null,
          });
        });
      });
      return flattened;
    },
    staleTime: 1000 * 30,
    refetchInterval: 30000,
  });

  // Fetch live time slots
  const { data: timeSlotsData = [] } = useQuery({
    queryKey: ['timeslots', visibleDateRange.startDate, visibleDateRange.endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: visibleDateRange.startDate,
        end_date: visibleDateRange.endDate,
        include_caretaker: 'true',
      });
      const res = await api.get(`/timeslots?${params}`);
      return res.data.slots || [];
    },
    staleTime: 1000 * 30,
    refetchInterval: 30000,
  });

  // Fetch planned snapshots
  const { data: snapshotsData = [] } = useQuery({
    queryKey: ['planned-snapshots', visibleDateRange.startDate, visibleDateRange.endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: visibleDateRange.startDate,
        end_date: visibleDateRange.endDate,
      });
      const res = await api.get(`/planned-snapshots?${params}`);
      return res.data.snapshots || [];
    },
    staleTime: 1000 * 30,
    refetchInterval: 30000,
  });

  // Build snapshot lookup by date
  const snapshotMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const s of snapshotsData as any[]) {
      map.set(s.date, s.data);
    }
    return map;
  }, [snapshotsData]);

  // Generate calendar grid dates
  const gridDates = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  // Cutoff: don't show history from before this date
  const historyCutoff = new Date('2026-02-16T00:00:00');

  // Compute actual (live) DayData
  const actualDays: DayData[] = useMemo(() => {
    return gridDates.map((date) => {
      const dateString = format(date, 'yyyy-MM-dd');
      const inCurrentMonth = isSameMonth(date, currentDate);
      const dayDate = new Date(date);
      dayDate.setHours(0, 0, 0, 0);
      const isBeforeCutoff = dayDate < historyCutoff;

      const allDayWindows = inCurrentMonth && !isBeforeCutoff
        ? (availabilityData || []).filter((w: any) => w.date === dateString)
        : [];

      const daySlots = isBeforeCutoff
        ? []
        : (timeSlotsData || []).filter((s: any) => s.date === dateString);

      // Split auto-assign windows into virtual slots
      const regularWindows: any[] = [];
      const virtualSlots: any[] = [];

      for (const w of allDayWindows) {
        if (w.auto_assign_user_id && w.auto_assign_user) {
          const hasRealSlot = daySlots.some(
            (s: any) => s.start_time === w.start_time && s.end_time === w.end_time
          );
          if (!hasRealSlot) {
            virtualSlots.push({
              id: `default-${dateString}-${w.start_time}-${w.end_time}`,
              caretaker_id: w.auto_assign_user_id,
              date: dateString,
              start_time: w.start_time,
              end_time: w.end_time,
              status: 'claimed',
              is_default: true,
              caretaker: w.auto_assign_user,
            });
          }
        } else {
          regularWindows.push(w);
        }
      }

      return {
        date,
        dateString,
        isCurrentMonth: inCurrentMonth,
        isToday: isToday(date),
        availabilityWindows: regularWindows,
        claimedSlots: [...daySlots, ...virtualSlots],
      };
    });
  }, [gridDates, currentDate, availabilityData, timeSlotsData]);

  // Compute planned DayData — uses snapshots when available, else falls back to live
  const plannedDays: DayData[] = useMemo(() => {
    return gridDates.map((date, idx) => {
      const dateString = format(date, 'yyyy-MM-dd');
      const dayDate = new Date(date);
      dayDate.setHours(0, 0, 0, 0);
      const isBeforeCutoff = dayDate < historyCutoff;

      // Before cutoff: return empty day
      if (isBeforeCutoff) {
        return actualDays[idx];
      }

      const snapshot = snapshotMap.get(dateString);

      if (!snapshot) {
        // No snapshot → fall back to live data
        return actualDays[idx];
      }

      const inCurrentMonth = isSameMonth(date, currentDate);

      // Parse snapshot windows into availability + virtual slots
      const snapshotWindows = snapshot.availability_windows || [];
      const snapshotSlots = snapshot.time_slots || [];

      const regularWindows: any[] = [];
      const virtualSlots: any[] = [];

      for (const w of snapshotWindows) {
        if (w.auto_assign_user_id && w.auto_assign_user) {
          const hasRealSlot = snapshotSlots.some(
            (s: any) => s.start_time === w.start && s.end_time === w.end
          );
          if (!hasRealSlot) {
            virtualSlots.push({
              id: `snap-default-${dateString}-${w.start}-${w.end}`,
              caretaker_id: w.auto_assign_user_id,
              date: dateString,
              start_time: w.start,
              end_time: w.end,
              status: 'claimed',
              is_default: true,
              caretaker: w.auto_assign_user,
            });
          }
        } else {
          regularWindows.push({
            date: dateString,
            start_time: w.start,
            end_time: w.end,
          });
        }
      }

      return {
        date,
        dateString,
        isCurrentMonth: inCurrentMonth,
        isToday: isToday(date),
        availabilityWindows: regularWindows,
        claimedSlots: [...snapshotSlots, ...virtualSlots],
      };
    });
  }, [gridDates, currentDate, snapshotMap, actualDays]);

  // Extract unique caretakers across both calendars for legend
  const caretakersInView = useMemo(() => {
    const map = new Map<string, any>();
    const addFromSlots = (slots: any[]) => {
      for (const s of slots) {
        if (s.caretaker && s.status !== 'cancelled' && !map.has(s.caretaker_id)) {
          map.set(s.caretaker_id, {
            id: s.caretaker_id,
            first_name: s.caretaker.first_name,
            color: s.caretaker.color,
            emoji: s.caretaker.emoji,
            role: s.caretaker.role,
          });
        }
      }
    };

    for (const day of actualDays) addFromSlots(day.claimedSlots as any[]);
    for (const day of plannedDays) addFromSlots(day.claimedSlots as any[]);

    return Array.from(map.values());
  }, [actualDays, plannedDays]);

  const handlePrevMonth = () => setCurrentDate((prev) => subMonths(prev, 1));
  const handleNextMonth = () => setCurrentDate((prev) => addMonths(prev, 1));
  const handleToday = () => setCurrentDate(startOfMonth(new Date()));

  return (
    <div className="bg-card rounded-xl border border-slate-700 p-6">
      {/* Header + Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">
          Planned vs Actual
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400 hidden sm:inline">
            {monthName}
          </span>
          <button
            onClick={handleToday}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            <Calendar size={12} />
            Today
          </button>
          <button
            onClick={handlePrevMonth}
            className="p-1 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={handleNextMonth}
            className="p-1 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Dual calendars with section headers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Planned Schedule */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-indigo-500/20">
              <Calendar size={14} className="text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-indigo-300">Planned Schedule</h3>
              <p className="text-[10px] text-gray-500">What was scheduled before the day started</p>
            </div>
          </div>
          <MiniCalendar
            title=""
            days={plannedDays}
            currentDate={currentDate}
            mode="planned"
          />
        </div>

        {/* What Actually Happened */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-emerald-500/20">
              <Clock size={14} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-emerald-300">What Actually Happened</h3>
              <p className="text-[10px] text-gray-500">Based on clock in/out times</p>
            </div>
          </div>
          <MiniCalendar
            title=""
            days={actualDays}
            currentDate={currentDate}
            mode="actual"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-slate-700 text-xs">
        {/* Caretaker colors */}
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span className="text-gray-400">Available</span>
        </div>
        {caretakersInView.map((ct) => (
          <div key={ct.id} className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: getUserColor(ct) }}
            />
            <span className="text-gray-400 flex items-center gap-0.5">
              {ct.emoji && <span>{ct.emoji}</span>}
              {ct.first_name}
            </span>
          </div>
        ))}
        {/* Status indicators divider */}
        <div className="w-px h-4 bg-slate-700" />
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/30 border border-emerald-500" />
          <span className="text-gray-400">On time</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-500/30 border border-amber-500" />
          <span className="text-gray-400">Late</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-500/30 border border-red-500" />
          <span className="text-gray-400">No show</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-500/30 border border-blue-500 animate-pulse" />
          <span className="text-gray-400">Live</span>
        </div>
      </div>
    </div>
  );
}
